import { persistentAtom } from '@nanostores/persistent'
import { atom, computed } from 'nanostores'

import type { Contact } from '../types'

const enum Column {
	Actions = 'actions',
	Added = 'added',
	Contact = 'contact',
	Custom1 = 'custom1',
	Custom2 = 'custom2',
	Custom3 = 'custom3',
	Headline = 'headline',
	Imported = 'imported',
	LastAction = 'last-action',
	Location = 'location',
	Status = 'status',
	PositionTitle = 'position-title',
	CompanyName = 'company-name',
}

const enum Size {
	Button = 1.8,
	Checkbox = 3.125,
	Gap = 0.4,
	SidePadding = 1
}

function createStore(key: string, fallbacks: Column[]) {
	const initial = localStorage.getItem(key)
	const parsed: Column[] = initial ? JSON.parse(initial) : fallbacks
	const $store = atom(parsed)
	$store.listen(nextValues =>
		localStorage.setItem(key, JSON.stringify(nextValues))
	)

	return $store
}

const ColumnCollection = {
	Campaign: [
		Column.Contact,
		Column.Headline,
		Column.Location,
		Column.Status,
		Column.LastAction,
		Column.Added
	],
	Customs: [Column.Custom1, Column.Custom2, Column.Custom3],
	General: [
		Column.Contact,
		Column.Headline,
		Column.Location,
		Column.LastAction,
		Column.PositionTitle,
		Column.CompanyName,
		Column.Imported
	]
}

const Options = {
	Campaign: [...ColumnCollection.Campaign, ...ColumnCollection.Customs],
	General: [...ColumnCollection.General, ...ColumnCollection.Customs]
}

const jsonOptions = {
	decode: JSON.parse,
	encode: JSON.stringify
}

const $contactColumns = persistentAtom<Column[]>(
	'selected-cols:c',
	ColumnCollection.General,
	jsonOptions
)

const $executionsColumns = persistentAtom<Column[]>(
	'selected-cols:e',
	ColumnCollection.Campaign,
	jsonOptions
)

const campaignContactStore = createStore(
	'campaignContactsCols',
	ColumnCollection.Campaign
)

const wideColumns = new Set([Column.Contact, Column.Headline])

function parseToTemplateRow(columns: string[], hasActions = false) {
	const templateRows: string[] = [`${Size.Checkbox}rem`]

	for (const key of columns) {
		const fallback = wideColumns.has(key) ? 'wide-cell-size' : 'cell-size'
		templateRows.push(`var(--${key}, var(--${fallback}))`)
	}

	hasActions && templateRows.push('auto')
	return templateRows.join(' ')
}

const $addContactsModalStyles = computed($contactColumns, columns =>
	parseToTemplateRow(columns)
)

const $contactStyles = computed($contactColumns, columns =>
	parseToTemplateRow(columns, true)
)

const $draftCampaignStyles = computed($executionsColumns, columns =>
	parseToTemplateRow(columns, true)
)
const $executionsStyles = computed($executionsColumns, columns =>
	parseToTemplateRow(columns, true)
)

const columnHeaders: Record<Column, string> = {
	[Column.Actions]: 'Actions',
	[Column.Added]: 'Added',
	[Column.Contact]: 'Contact',
	[Column.Custom1]: 'Custom 1',
	[Column.Custom2]: 'Custom 2',
	[Column.Custom3]: 'Custom 3',
	[Column.Headline]: 'Headline',
	[Column.Imported]: 'Imported',
	[Column.LastAction]: 'Last action',
	[Column.Location]: 'Location',
	[Column.PositionTitle]: 'Title',
	[Column.CompanyName]: 'Company',
	[Column.Status]: 'Status'
}

type SortKey =
	| keyof Contact
	| 'last_job/title'
	| 'last_job/company/name';

const sortKeys: Partial<Record<Column, SortKey>> = {
	[Column.Added]: 'created',
	[Column.Contact]: 'full_name',
	[Column.Custom1]: 'custom_field_1',
	[Column.Custom2]: 'custom_field_2',
	[Column.Custom3]: 'custom_field_3',
	[Column.Headline]: 'occupation',
	[Column.Imported]: 'imported',
	[Column.LastAction]: 'contacted',
	[Column.Location]: 'location',
	[Column.PositionTitle]: 'last_job/title',
	[Column.CompanyName]: 'last_job/company/name'
}

export {
	$addContactsModalStyles,
	$contactColumns,
	$contactStyles,
	$draftCampaignStyles,
	$executionsColumns,
	$executionsStyles,
	campaignContactStore,
	Column,
	ColumnCollection,
	columnHeaders,
	Options,
	sortKeys
}
